// frontend/src/pages/Analytics/ContractTicketVolume.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Typography, Row, Col, Select, DatePicker, Button, Table, 
  Spin, Alert, Statistic, Space, Tag, Divider, Progress, Empty,
  Radio, message
} from 'antd';
import {
  BarChartOutlined, FileExcelOutlined, FileWordOutlined,
  ReloadOutlined, RiseOutlined, FallOutlined,
  PieChartOutlined, TableOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import api from '../../utils/api';
import './Analytics.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const ContractTicketVolume = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const notificationApi = useNotificationApi();

  // State
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [selectedContracts, setSelectedContracts] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [activeTab, setActiveTab] = useState('table');
  const [exportLoading, setExportLoading] = useState(false);

  // Fetch contracts for filter dropdown
  useEffect(() => {
    fetchContracts();
  }, []);

  // Fetch report data when filters change (both selections made)
  useEffect(() => {
    if (dateRange && dateRange.length === 2 && selectedContracts !== null) {
      const startMonth = dateRange[0];
      const endMonth = dateRange[1];
      fetchReportData(startMonth, endMonth);
    } else {
      setReportData(null);
    }
  }, [selectedContracts, dateRange]);

  const showError = useCallback((title, content, key = 'analyticsError') => {
    notificationApi.error({
      key,
      message: title,
      description: content,
      duration: 4.5,
      placement: 'topRight',
    });
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'analyticsSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
    });
  }, [notificationApi]);

  const fetchContracts = async () => {
    try {
      const response = await api.get('/api/contracts');
      if (response.data.success) {
        setContracts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  const fetchReportData = async (start, end) => {
    if (!start || !end || selectedContracts === null) return;
    
    setLoading(true);
    try {
      const startMonthStr = start.format('YYYY-MM');
      const endMonthStr = end.format('YYYY-MM');
      
      const params = {
        startMonth: startMonthStr,
        endMonth: endMonthStr,
        contractIds: selectedContracts === 'all' ? 'all' : selectedContracts
      };
      
      const response = await api.get('/api/analytics/contract-ticket-volume', { params });
      
      if (response.data.success) {
        setReportData(response.data.data);
      } else {
        showError('Failed to Load Report', response.data.message || 'Unable to load report data');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      showError('Report Error', error.response?.data?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = async () => {
    if (!reportData) {
      showError('Export Failed', 'No data to export. Please select date range and contract first.');
      return;
    }
    
    if (!dateRange || dateRange.length !== 2) {
      showError('Export Failed', 'Please select a valid date range.');
      return;
    }
    
    setExportLoading(true);
    try {
      const response = await api.post('/api/analytics/contract-ticket-volume/export/excel', {
        startMonth: dateRange[0].format('YYYY-MM'),
        endMonth: dateRange[1].format('YYYY-MM'),
        contractIds: selectedContracts,
        reportData: reportData
      }, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contract_Ticket_Volume_Report_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccess('Export Successful', 'Report exported to Excel successfully.');
    } catch (error) {
      console.error('Export error:', error);
      showError('Export Failed', error.response?.data?.message || 'Failed to export report');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportToWord = async () => {
    if (!reportData) {
      showError('Export Failed', 'No data to export. Please select date range and contract first.');
      return;
    }
    
    if (!dateRange || dateRange.length !== 2) {
      showError('Export Failed', 'Please select a valid date range.');
      return;
    }
    
    setExportLoading(true);
    try {
      const response = await api.post('/api/analytics/contract-ticket-volume/export/word', {
        startMonth: dateRange[0].format('YYYY-MM'),
        endMonth: dateRange[1].format('YYYY-MM'),
        contractIds: selectedContracts,
        reportData: reportData
      }, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contract_Ticket_Volume_Report_${dayjs().format('YYYYMMDD_HHmmss')}.doc`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccess('Export Successful', 'Report exported to Word successfully.');
    } catch (error) {
      console.error('Export error:', error);
      showError('Export Failed', error.response?.data?.message || 'Failed to export report');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    } else {
      setDateRange(null);
    }
  };

  // Handle contract selection change
  const handleContractChange = (value) => {
    setSelectedContracts(value);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setDateRange(null);
    setSelectedContracts(null);
    setReportData(null);
  };

  // Disable future months
  const disabledDate = (current) => {
    return current && current > dayjs().endOf('month');
  };

  // Table columns
  const getTableColumns = () => {
    if (!reportData || !reportData.contracts || reportData.contracts.length === 0) return [];
    
    const columns = [
      {
        title: 'Month',
        dataIndex: 'month',
        key: 'month',
        fixed: 'left',
        width: 120,
        render: (month) => {
          const monthName = dayjs(month, 'YYYY-MM').format('MMMM YYYY');
          return <Text strong>{monthName}</Text>;
        }
      },
      ...reportData.contracts.map(contract => ({
        title: contract,
        dataIndex: ['summaryTable', contract],
        key: contract,
        align: 'center',
        width: 100,
        render: (value, record) => {
          const month = record.month;
          const count = reportData.summaryTable[contract]?.[month] || 0;
          return (
            <Tag color={count > 0 ? 'blue' : 'default'} style={{ fontSize: 14 }}>
              {count}
            </Tag>
          );
        }
      })),
      {
        title: 'Monthly Total',
        key: 'monthlyTotal',
        align: 'center',
        width: 100,
        fixed: 'right',
        render: (_, record) => {
          const total = reportData.monthlyTotals?.[record.month] || 0;
          return <Text strong style={{ color: '#1890ff' }}>{total}</Text>;
        }
      }
    ];
    
    return columns;
  };

  // Table data source
  const getTableDataSource = () => {
    if (!reportData?.months) return [];
    return reportData.months.map(month => ({ month, key: month }));
  };

  // Summary table (contract totals)
  const summaryColumns = [
    {
      title: 'Contract',
      dataIndex: 'contract',
      key: 'contract',
      width: 200,
    },
    {
      title: 'Total Tickets',
      dataIndex: 'count',
      key: 'count',
      align: 'center',
      width: 120,
      render: (count) => <Tag color="blue" style={{ fontSize: 16, fontWeight: 'bold' }}>{count}</Tag>
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'center',
      width: 200,
      render: (percentage) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress 
            percent={percentage} 
            size="small" 
            style={{ flex: 1, minWidth: 100 }}
            strokeColor="#1890ff"
          />
          <Text strong>{percentage}%</Text>
        </div>
      )
    }
  ];

  const summaryDataSource = () => {
    if (!reportData?.contractRatios) return [];
    return Object.entries(reportData.contractRatios).map(([contract, data]) => ({
      contract,
      count: data.count,
      percentage: parseFloat(data.percentage),
      key: contract
    }));
  };

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <Title level={2} className="analytics-title">Contract Ticket Volume Report</Title>
        <Text className="analytics-description">
          Select date range and contract to view ticket volume analysis.
        </Text>
      </div>

      <Card className="filter-card">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={8} lg={8}>
            <div className="filter-label">
              <Text strong>Date Range (Month/Year)</Text>
            </div>
            <RangePicker
              picker="month"
              value={dateRange}
              onChange={handleDateRangeChange}
              format="MMM YYYY"
              style={{ width: '100%' }}
              placeholder={['Start', 'End']}
              disabledDate={disabledDate}
              allowClear={true}
            />
          </Col>
          <Col xs={24} sm={10} md={10} lg={10}>
            <div className="filter-label">
              <Text strong>Select Contract</Text>
            </div>
            <Select
              value={selectedContracts}
              onChange={handleContractChange}
              style={{ width: '100%' }}
              placeholder="-- Select Contract --"
              showSearch
              allowClear={true}
              optionFilterProp="children"
            >
              <Option value="all">All Active Contracts</Option>
              {contracts.map(contract => (
                <Option key={contract.id} value={contract.id}>
                  {contract.contract_name || contract.contractName}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={4} md={4} lg={4}>
            <div className="filter-label">
              <span style={{ display: 'block', visibility: 'hidden' }}>.</span>
            </div>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleClearFilters}
              size="small"
              style={{ width: '100%' }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div className="loading-container">
          <Spin size="large" tip="Generating report..." />
        </div>
      ) : reportData ? (
        <>
          {/* Summary Cards */}
          <Row gutter={[16, 16]} className="summary-cards">
            <Col xs={24} sm={12} md={6}>
              <Card className="stat-card">
                <Statistic
                  title="Total Tickets"
                  value={reportData.grandTotal}
                  prefix={<BarChartOutlined />}
                  valueStyle={{ color: '#1890ff', fontSize: 32 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="stat-card">
                <Statistic
                  title="Date Range"
                  value={dateRange ? `${dateRange[0].format('MMMM YYYY')} - ${dateRange[1].format('MMMM YYYY')}` : '-'}
                  valueStyle={{ fontSize: 14 }}
                />
                <Text type="secondary">{reportData.months?.length || 0} months</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="stat-card">
                <Statistic
                  title="Peak Month"
                  value={reportData.insights?.highestMonth?.month ? dayjs(reportData.insights.highestMonth.month, 'YYYY-MM').format('MMMM YYYY') : 'N/A'}
                  prefix={<RiseOutlined style={{ color: '#ff4d4f' }} />}
                />
                <Text type="secondary">{reportData.insights?.highestMonth?.count || 0} tickets</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="stat-card">
                <Statistic
                  title="Lowest Month"
                  value={reportData.insights?.lowestMonth?.month ? dayjs(reportData.insights.lowestMonth.month, 'YYYY-MM').format('MMMM YYYY') : 'N/A'}
                  prefix={<FallOutlined style={{ color: '#52c41a' }} />}
                />
                <Text type="secondary">{reportData.insights?.lowestMonth?.count || 0} tickets</Text>
              </Card>
            </Col>
          </Row>

          {/* Top Contract Insight */}
          {reportData.insights?.topContract && (
            <Alert
              message="Key Insight"
              description={`${reportData.insights.topContract[0]} represents ${reportData.insights.topContract[1]?.percentage || 0}% of all tickets in this period.`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Tabs for different views */}
          <Card className="report-card">
            <div className="report-actions">
              <Radio.Group value={activeTab} onChange={(e) => setActiveTab(e.target.value)} buttonStyle="solid">
                <Radio.Button value="table">
                  <TableOutlined /> Monthly Breakdown
                </Radio.Button>
                <Radio.Button value="summary">
                  <PieChartOutlined /> Summary by Contract
                </Radio.Button>
              </Radio.Group>
              <Space>
                <Button 
                  icon={<FileExcelOutlined />} 
                  onClick={handleExportToExcel}
                  loading={exportLoading}
                >
                  Export to Excel
                </Button>
                <Button 
                  icon={<FileWordOutlined />} 
                  onClick={handleExportToWord}
                  loading={exportLoading}
                >
                  Export to Word
                </Button>
              </Space>
            </div>

            {activeTab === 'table' && (
              <Table
                columns={getTableColumns()}
                dataSource={getTableDataSource()}
                loading={loading}
                pagination={false}
                bordered
                size="middle"
                scroll={{ x: 'max-content' }}
                className="report-table"
              />
            )}

            {activeTab === 'summary' && (
              <Table
                columns={summaryColumns}
                dataSource={summaryDataSource()}
                loading={loading}
                pagination={false}
                bordered
                size="middle"
                className="report-table"
              />
            )}

            <div className="report-footer">
              <Text type="secondary">
                Generated on {dayjs().format('DD MMM YYYY, hh:mm A')} by {user?.firstName} {user?.lastName}
              </Text>
            </div>
          </Card>
        </>
      ) : (
        <Card className="empty-card">
          <Empty
            description="Please select date range and contract to view the report."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}
    </div>
  );
};

export default ContractTicketVolume;